import React from 'react';

import { InputLabel, Stack, TextField, TextFieldProps } from '@mui/material';
import { FieldHookConfig, useField } from 'formik';

type IProps = TextFieldProps &
  FieldHookConfig<string> & {
    label?: string;
    placeholder?: string;
    dynamicWidth?: boolean;
  };

const FormText = React.memo(
  ({ label, placeholder, variant = 'outlined', dynamicWidth, ...props }: IProps) => {
    const [inputWidth, setInputWidth] = React.useState<number | undefined>(undefined);

    const [field, meta] = useField(props);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const measureRef = React.useRef<HTMLSpanElement>(null);

    const isError = meta.touched && Boolean(meta.error);

    React.useEffect(() => {
      if (dynamicWidth && inputRef.current && containerRef.current && measureRef.current) {
        const textToMeasure = field.value || placeholder || '';

        const styles = window.getComputedStyle(inputRef.current);
        measureRef.current.style.fontSize = styles.fontSize;
        measureRef.current.style.fontFamily = styles.fontFamily;
        measureRef.current.style.fontWeight = styles.fontWeight;
        measureRef.current.style.letterSpacing = styles.letterSpacing;

        measureRef.current.textContent = textToMeasure;
        const textWidth = measureRef.current.offsetWidth;

        const calculatedWidth = textWidth + 28 + 4;
        const containerWidth = containerRef.current.offsetWidth;
        const finalWidth = Math.min(calculatedWidth, containerWidth);

        setInputWidth(finalWidth);
      }
    }, [field.value, placeholder, dynamicWidth]);

    return (
      <Stack
        ref={containerRef}
        spacing={0.5}
        {...(props.fullWidth && { width: '100%' })}
        sx={{
          ...(dynamicWidth && {
            maxWidth: '100%',
            width: '100%',
          }),
        }}
      >
        {label && <InputLabel {...(isError && { color: 'error' })}>{label}</InputLabel>}

        {dynamicWidth && (
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'pre',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
            }}
            aria-hidden="true"
          />
        )}

        <TextField
          variant={variant}
          autoComplete="off"
          {...field}
          {...props}
          placeholder={placeholder}
          error={isError}
          helperText={meta.touched && meta.error}
          inputRef={inputRef}
          data-testid={`form-text-${props.name}`}
          sx={{
            ...(dynamicWidth &&
              inputWidth && {
                width: `${inputWidth}px`,
                maxWidth: '100%',
                minWidth: '70px',
              }),
            ...props.sx,
          }}
        />
      </Stack>
    );
  },
);

export default FormText;
